<template>
  <div class="relative isolate flex h-full flex-col pb-4">
    <slot name="title">
      <div
        class="sticky top-0 z-40 flex w-full items-center gap-2 bg-white p-3 px-5 leading-none text-black md:py-4"
      >
        <h2 v-if="!activeOptionTitle" class="text-lg font-bold">
          {{ t('product.options.text_option') }}&nbsp;{{
            t('product.options.color')
          }}
        </h2>
        <span v-else class="truncate text-base font-normal">
          <span class="font-bold">{{ t('product.options.color') }}</span
          >: {{ activeOptionTitle }}
        </span>
      </div>
    </slot>

    <div class="size-full grow overflow-y-auto">
      <div ref="optionsList">
        <div v-if="computedOptionsInStock.length > 0">
          <div class="ml-px mt-px grid md:px-5" :class="gridClass">
            <GalleryOptionItem
              v-for="(option, index) in computedOptionsInStock"
              :key="'option-' + index"
              :option="option"
              :is-inside-modal="true"
              :is-active="!!activeOptionId && activeOptionId == option.id"
              @on-click="($event) => $emit('onClick', $event)"
            />
          </div>
        </div>
      </div>

      <h2
        v-if="computedOptionsOutOfStock.length > 0"
        class="mb-4 mt-10 px-5 text-lg font-bold text-black"
      >
        {{ t('product.stock.text_oos') }}
      </h2>

      <div
        v-if="computedOptionsOutOfStock.length > 0"
        class="ml-px grid max-xs:grid-cols-3 md:px-5"
        :class="gridClass"
      >
        <GalleryOptionItem
          v-for="(option, index) in computedOptionsOutOfStock"
          :key="'option-' + index"
          :option="option"
          :is-inside-modal="true"
          :is-active="!!activeOptionId && activeOptionId == option.id"
          @on-click="($event) => $emit('onClick', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import { useTranslations } from '@/shared/composables/useTranslations.ts';

import { useProductOptionsStore } from '../../shared/pinia/modules/product-options.ts';
import GalleryOptionItem from './GalleryOptionItem.vue';

const { t } = useTranslations();
const store = useProductOptionsStore();

type GalleryOption = {
  id: number;
  name: string;
  title: string;
  inStock: boolean;
  disabled?: boolean;
  discontinued?: boolean;
  colorCode?: string;
  consolidatedImageUrl?: string;
  price?: number;
  compare_at_price?: number | null;
};

const props = defineProps<{
  activeOptionId?: number | null;
  activeOptionTitle?: string | '';
  optionsInStock?: Array<GalleryOption>;
  optionsOutOfStock?: Array<GalleryOption>;
}>();

defineEmits(['onClick', 'on-close']);

const optionsList = ref<HTMLElement | null>(null);

// Use props if provided, otherwise fall back to store
const computedOptionsInStock = computed(() => {
  return props.optionsInStock ?? store.optionsInStock;
});

const computedOptionsOutOfStock = computed(() => {
  return props.optionsOutOfStock ?? store.optionsOutOfStock;
});

const gridClass = computed(() => {
  return computedOptionsInStock.value.length <= 15
    ? 'grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
    : 'max-xs:grid-cols-3 grid-cols-4 md:grid-cols-6';
});

const isUsingStore = computed(() => {
  return !props.optionsInStock && !props.optionsOutOfStock;
});

let timeout: number | undefined;

const onScroll = () => {
  // Only store scroll position if using store (not when options are passed as props)
  if (isUsingStore.value) {
    store.modalOptionsPosition =
      optionsList.value?.parentElement?.scrollTop ?? 0;
  }
};

onMounted(() => {
  optionsList.value?.parentElement?.addEventListener('scroll', onScroll, {
    passive: true
  });

  // Only restore scroll position if using store (not when options are passed as props)
  if (isUsingStore.value) {
    const optionsPosition = store.modalOptionsPosition;
    // NOTE: We need to scroll after modal animation
    timeout = window.setTimeout(() => {
      optionsList.value?.parentElement?.scrollTo({
        left: 0,
        top: optionsPosition
      }); // , behavior: 'smooth'
    }, 200);
  }
});

onBeforeUnmount(() => {
  optionsList.value?.parentElement?.removeEventListener('scroll', onScroll);
  if (timeout) {
    window.clearTimeout(timeout);
  }
});
</script>
